<template>
  <div class="chart-card">
    <div class="chart-card__header">
      <div>
        <h3 class="chart-card__title">{{ title }}</h3>
        <p class="chart-card__subtitle">{{ subtitle }}</p>
      </div>
      <div class="chart-card__tabs" v-if="showTabs">
        <button
          v-for="(tab, index) in tabs"
          :key="index"
          :class="['chart-card__tab', { 'chart-card__tab--active': activeTab === index }]"
          @click="$emit('tab-change', index)"
        >
          {{ tab }}
        </button>
      </div>
    </div>
    <div class="chart-card__body">
      <div v-if="products.length === 0" class="chart-card__empty">
        <font-awesome-icon icon="box-open" class="chart-card__empty-icon" />
        <p>Ingen data tilgjengelig</p>
      </div>
      <div v-else class="products-list">
        <div
          v-for="(product, index) in products"
          :key="index"
          class="product-row"
        >
          <div class="product-row__rank" :class="getRankClass(index)">
            {{ index + 1 }}
          </div>
          <div class="product-row__info">
            <span class="product-row__name">{{ product.name }}</span>
            <span class="product-row__category">{{ product.category }}</span>
          </div>
          <div class="product-row__stats">
            <span class="product-row__value">{{ formatCurrency(product.revenue) }}</span>
            <span class="product-row__quantity">{{ product.quantity }} solgt</span>
          </div>
          <div class="product-row__bar-container">
            <div
              class="product-row__bar"
              :style="{ width: getBarWidth(product.revenue) + '%' }"
              :class="getBarClass(index)"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TopProductsChart',
  props: {
    title: { type: String, default: 'Topp produkter' },
    subtitle: { type: String, default: 'Basert på omsetning' },
    products: { type: Array, default: () => [] },
    showTabs: { type: Boolean, default: true },
    tabs: { type: Array, default: () => ['Topp 10', 'Bunn 10'] },
    activeTab: { type: Number, default: 0 }
  },
  emits: ['tab-change'],
  computed: {
    maxRevenue() {
      if (this.products.length === 0) return 0;
      return Math.max(...this.products.map(p => p.revenue));
    }
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('nb-NO').format(Math.round(value)) + ' kr';
    },
    getBarWidth(revenue) {
      if (this.maxRevenue === 0) return 0;
      return (revenue / this.maxRevenue) * 100;
    },
    getRankClass(index) {
      if (this.activeTab === 1) return ''; // Bottom products don't get medals
      if (index === 0) return 'product-row__rank--gold';
      if (index === 1) return 'product-row__rank--silver';
      if (index === 2) return 'product-row__rank--bronze';
      return '';
    },
    getBarClass(index) {
      if (this.activeTab === 1) return 'product-row__bar--bottom';
      if (index < 3) return 'product-row__bar--top';
      return '';
    }
  }
};
</script>

<style scoped>
.chart-card {
  background: var(--bo-surface);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 20px 0 20px;
}

.chart-card__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0 0 4px 0;
}

.chart-card__subtitle {
  font-size: 13px;
  color: var(--bo-muted);
  margin: 0;
}

.chart-card__tabs {
  display: flex;
  gap: 4px;
  background: var(--bo-surface-2);
  padding: 4px;
  border-radius: 8px;
}

.chart-card__tab {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--bo-muted);
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.chart-card__tab:hover {
  color: var(--bo-ink);
}

.chart-card__tab--active {
  background: var(--bo-surface);
  color: var(--bo-alert-ink);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-card__body {
  padding: 20px;
}

.chart-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--bo-muted-2);
}

.chart-card__empty-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.products-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-row {
  display: grid;
  grid-template-columns: 32px 1fr auto 100px;
  gap: 12px;
  align-items: center;
  padding: 8px 0;
}

.product-row__rank {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--bo-muted);
  background: var(--bo-surface-2);
  border-radius: 8px;
}

.product-row__rank--gold {
  background: linear-gradient(135deg, #ffd700 0%, var(--bo-brand-tint) 100%);
  color: #8b6914;
}

.product-row__rank--silver {
  background: linear-gradient(135deg, var(--bo-muted-2) 0%, var(--bo-line) 100%);
  color: #5a5a5a;
}

.product-row__rank--bronze {
  background: linear-gradient(135deg, #cd7f32 0%, #daa06d 100%);
  color: #6b4423;
}

.product-row__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.product-row__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--bo-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-row__category {
  font-size: 12px;
  color: var(--bo-muted-2);
}

.product-row__stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.product-row__value {
  font-size: 14px;
  font-weight: 600;
  color: var(--bo-ink);
}

.product-row__quantity {
  font-size: 12px;
  color: var(--bo-muted-2);
}

.product-row__bar-container {
  height: 8px;
  background: var(--bo-surface-2);
  border-radius: 4px;
  overflow: hidden;
}

.product-row__bar {
  height: 100%;
  background: #B30E10;
  border-radius: 4px;
  opacity: 0.6;
  transition: width 0.3s ease;
}

.product-row__bar--top {
  opacity: 1;
}

.product-row__bar--bottom {
  background: var(--bo-muted-2);
}

@media (max-width: 768px) {
  .product-row {
    grid-template-columns: 28px 1fr auto;
  }

  .product-row__bar-container {
    display: none;
  }
}
</style>
